import { DATA_WORKSPACE_ENABLED } from "../../config/dataWorkspaceFeature";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Tooltip,
  Button,
  Divider,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ScienceOutlined as ScienceOutlinedIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../scenes/utils/axiosInstance';
import { isDoctorSessionActive } from '../../utils/authSession';
import { API_PREFIX } from '../../config/api';
import { socket } from '../../socket';
import { createStatsWorker } from '../../workers/statsWorkerFactory';
import { tokens } from '../../theme';
import { PANEL } from '../../design-system/panel/panelTokens';
import { getCatalogTest, getTestLocalizedName } from '../../scenes/statistical-analysis/data/mockStatisticalData';
import {
  panelDropdownTriggerSx,
  panelTopbarActionIconSx,
  panelDropdownPopoverSlotProps,
  panelDropdownPopoverModalProps,
  panelDropdownHeaderSx,
  panelDropdownHeaderTitleSx,
  panelDropdownHeaderActionSx,
  panelDropdownListSx,
  panelDropdownItemRowSx,
  panelDropdownItemButtonSx,
  panelDropdownItemIconWrapSx,
  panelDropdownItemTitleSx,
  panelDropdownItemTimeSx,
  panelDropdownDividerSx,
  panelDropdownEmptySx,
  panelDropdownEmptyTitleSx,
  panelDropdownFooterSx,
  panelDropdownFooterButtonSx,
  panelDropdownBadgeSx,
} from '../../design-system/panel/panelDropdownSx';
import {
  CLEANING_JOB_TYPE,
  applyCleaningSocket,
  buildCleaningQueuedJob,
  cleaningJobDetail,
  cleaningJobHref,
  cleaningJobTitle,
  cleaningTerminalTransitions,
  combineJobFeeds,
  isViewingCleaningDataset,
  readCleaningToastIds,
  rememberCleaningToastId,
} from '../../utils/cleaningJobPanel';


const STATUS_CONFIG = {
  queued:     { labelKey: 'statsJobStatusQueued',    color: 'warning', icon: <HourglassIcon fontSize="small" /> },
  processing: { labelKey: 'statsJobStatusProcessing', color: 'info',    icon: <CircularProgress size={14} /> },
  completed:  { labelKey: 'statsJobStatusCompleted', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  failed:     { labelKey: 'statsJobStatusFailed',    color: 'error',   icon: <ErrorIcon fontSize="small" /> },
  cancelled:  { labelKey: 'statsJobStatusCancelled', color: 'default', icon: <CancelIcon fontSize="small" /> },
};

const TEST_NAMES = {
  descriptive_stats: 'Betimsel İstatistikler',
  normality_test: 'Normallik Testi',
  independent_t_test: 'Bağımsız Örneklem T-Testi',
  mann_whitney_u: 'Mann-Whitney U Testi',
  paired_t_test: 'Bağımlı Örneklem T-Testi',
  wilcoxon: 'Wilcoxon İşaretli Sıralar Testi',
  anova: 'Tek Yönlü ANOVA',
  kruskal_wallis: 'Kruskal-Wallis H Testi',
  chi_square: 'Ki-Kare (Chi-Square) Testi',
  pearson_correlation: 'Pearson Korelasyonu',
  spearman_correlation: 'Spearman Korelasyonu',
  linear_regression: 'Doğrusal Regresyon',
  logistic_regression: 'Lojistik Regresyon',
  profile_dataset: 'Veri Seti Profili Çıkarma (EDA)',
  survival_analysis: 'Sağkalım Analizi (Kaplan-Meier)',
  cox_regression: 'Cox Regresyon',
  feature_impact: 'Özellik Etkisi (Feature Impact)',
};

/**
 * StatsJobBadge — İstatistik iş kuyruğu durum göstergesi.
 *
 * Props:
 *   onJobCompleted: fn      — Sonuç hazır olduğunda çağrılır { jobId, result }
 */
export default function StatsJobBadge({ onJobCompleted, variant = "theme" }) {
  const sessionActive = isDoctorSessionActive();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isPanel = variant === "panel";
  const { t, i18n } = useTranslation('common');
  const [anchorEl, setAnchorEl] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [cleaningToast, setCleaningToast] = useState(null);
  const socketRef = useRef(null);
  const pollingRef = useRef(null);
  const jobsRef = useRef([]);
  const toastedRef = useRef(null);
  const unknownFetchAt = useRef(0);
  const pathRef = useRef("");
  const tRef = useRef(t);
  const navigate = useNavigate();
  const location = useLocation();
  pathRef.current = location.pathname;
  tRef.current = t;
  if (toastedRef.current == null) toastedRef.current = readCleaningToastIds();

  const dateLocale = (i18n.resolvedLanguage || i18n.language || 'tr').startsWith('fr')
    ? 'fr-FR'
    : (i18n.resolvedLanguage || i18n.language || 'tr').startsWith('en')
      ? 'en-US'
      : 'tr-TR';
  const statsJobsLabel = t('statsJobs');
  const language = i18n.resolvedLanguage || i18n.language || 'tr';

  const flushCleaningToasts = useCallback((list) => {
    if (!DATA_WORKSPACE_ENABLED) return;
    for (const job of list) {
      const id = String(job.id);
      if (toastedRef.current.has(id)) continue;
      rememberCleaningToastId(toastedRef.current, id);
      if (isViewingCleaningDataset(pathRef.current, job.dataset_id)) continue;
      setCleaningToast({
        id,
        status: job.status,
        dataset_id: job.dataset_id,
        message: tRef.current(job.status === 'failed' ? 'jobs.cleaning.toastFailed' : 'jobs.cleaning.toastDone'),
      });
    }
  }, []);

  const commitJobs = useCallback((updater) => {
    const previous = jobsRef.current;
    const next = updater(previous);
    jobsRef.current = next;
    setJobs(next);
    flushCleaningToasts(cleaningTerminalTransitions(previous, next));
  }, [flushCleaningToasts]);

  const readFeed = (settled) => (
    settled.status === 'fulfilled' && settled.value?.data?.success && Array.isArray(settled.value.data.jobs)
      ? settled.value.data.jobs
      : []
  );

  const fetchJobs = useCallback(async () => {
    if (!sessionActive) return;
    try {
      setLoading(true);
      const [statsRes, mlRes, cleaningRes] = await Promise.allSettled([
        axiosInstance.get(`${API_PREFIX}/stats/jobs?limit=20`),
        axiosInstance.get(`${API_PREFIX}/machine-learning/jobs?limit=20`),
        DATA_WORKSPACE_ENABLED ? axiosInstance.get(`${API_PREFIX}/data-workspace/cleaning/jobs?limit=20`) : Promise.resolve(null),
      ]);
      const clearedAtStr = localStorage.getItem('statsJobsClearedAt');
      const clearedAt = clearedAtStr ? parseInt(clearedAtStr, 10) : 0;
      commitJobs((prev) => combineJobFeeds({
        statsJobs: readFeed(statsRes),
        mlJobs: readFeed(mlRes),
        cleaningJobs: readFeed(cleaningRes),
        previous: prev,
        clearedAt,
      }));
    } catch {
      // sessiz hata
    } finally {
      setLoading(false);
    }
  }, [sessionActive, commitJobs]);

  // WebSocket bağlantısı
  useEffect(() => {
    if (!sessionActive) return;
    
    socketRef.current = socket;

    const handleJobUpdate = (payload) => {
      const { jobId, status, result, error, progress } = payload;

      if (progress !== undefined) {
        setProgressMap((prev) => ({ ...prev, [jobId]: progress }));
      }

      commitJobs((prev) => {
        const existing = prev.find((j) => j.id === jobId);
        // Sadece cancelled olanları listeden çıkar
        if (status === 'cancelled') {
          setTimeout(() => commitJobs((p) => p.filter((j) => j.id !== jobId)), 3000);
        }
        if (existing) {
          return prev.map((j) => j.id === jobId ? { ...j, status } : j);
        }
        return prev;
      });

      // Clear real progress on completion/failure
      if (status !== 'processing' && status !== 'queued') {
         setProgressMap((prev) => {
           if (!prev[jobId]) return prev;
           const next = { ...prev };
           delete next[jobId];
           return next;
         });
      }

      if (status === 'completed' && result) {
        if (onJobCompleted) onJobCompleted({ jobId, result });
        // `envelope`: additive — job_context (test/kaynak künyesi) sonuçla birlikte taşınır.
        window.dispatchEvent(new CustomEvent('stats:job:completed', { detail: { jobId, result, envelope: payload } }));
      } else if (status === 'failed' || status === 'cancelled') {
        window.dispatchEvent(new CustomEvent('stats:job:failed', { detail: { jobId, error, status } }));
      } else if (status === 'processing' || status === 'queued') {
        window.dispatchEvent(new CustomEvent('stats:job:update', { detail: { jobId, status } }));
      }
    };

    socket.on('stats:job:processing', handleJobUpdate);
    socket.on('stats:job:progress', handleJobUpdate);
    socket.on('stats:job:completed', handleJobUpdate);
    socket.on('stats:job:failed', handleJobUpdate);
    socket.on('ml:job:processing', handleJobUpdate);
    socket.on('ml:job:progress', handleJobUpdate);
    socket.on('ml:job:completed', handleJobUpdate);
    socket.on('ml:job:failed', handleJobUpdate);

    const cleaningEventNames = ['cleaning:job:queued', 'cleaning:job:progress', 'cleaning:job:completed', 'cleaning:job:failed'];
    const handleCleaningEvent = (eventName) => (payload) => {
      let unknown = false;
      let changed = null;
      commitJobs((prev) => {
        const applied = applyCleaningSocket(prev, eventName, payload);
        unknown = applied.unknown;
        changed = applied.changed;
        return applied.jobs;
      });
      if (changed) {
        setProgressMap((prev) => {
          const id = changed.id;
          if (changed.status !== 'processing' && changed.status !== 'queued') {
            if (prev[id] == null) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
          }
          const value = changed.progress;
          if (value == null || (prev[id] != null && value < prev[id])) return prev;
          return { ...prev, [id]: value };
        });
      }
      if (unknown) {
        const now = Date.now();
        if (now - unknownFetchAt.current > 1000) {
          unknownFetchAt.current = now;
          fetchJobs();
        }
      }
    };
    const cleaningHandlers = (DATA_WORKSPACE_ENABLED ? cleaningEventNames : []).map((eventName) => [eventName, handleCleaningEvent(eventName)]);
    for (const [eventName, handler] of cleaningHandlers) socket.on(eventName, handler);

    const handleJobQueued = (e) => {
      const raw = e.detail?.job;
      if (!raw) return;
      const job = e.type === 'cleaning:job:queued'
        ? buildCleaningQueuedJob({
          id: raw.id,
          datasetId: raw.dataset_id,
          datasetName: raw.dataset_name,
          queuedAt: raw.queued_at,
        })
        : raw;
      if (!job?.id) return;
      commitJobs((prev) => {
        if (prev.find((item) => item.id === job.id)) return prev;
        return [job, ...prev].sort((a, b) => new Date(b.queued_at || 0) < new Date(a.queued_at || 0) ? -1 : 1).slice(0, 20);
      });
    };
    window.addEventListener('stats:job:queued', handleJobQueued);
    window.addEventListener('ml:job:queued', handleJobQueued);
    if (DATA_WORKSPACE_ENABLED) window.addEventListener('cleaning:job:queued', handleJobQueued);

    return () => {
      socket.off('stats:job:processing', handleJobUpdate);
      socket.off('stats:job:progress', handleJobUpdate);
      socket.off('stats:job:completed', handleJobUpdate);
      socket.off('stats:job:failed', handleJobUpdate);
      socket.off('ml:job:processing', handleJobUpdate);
      socket.off('ml:job:progress', handleJobUpdate);
      socket.off('ml:job:completed', handleJobUpdate);
      socket.off('ml:job:failed', handleJobUpdate);
      for (const [eventName, handler] of cleaningHandlers) socket.off(eventName, handler);
      window.removeEventListener('stats:job:queued', handleJobQueued);
      window.removeEventListener('ml:job:queued', handleJobQueued);
      window.removeEventListener('cleaning:job:queued', handleJobQueued);
    };
  }, [sessionActive, onJobCompleted, commitJobs, fetchJobs]);

  // İlk yükleme + periyodik poll (60 sn)
  useEffect(() => {
    fetchJobs();
    pollingRef.current = setInterval(fetchJobs, 60_000);
    return () => clearInterval(pollingRef.current);
  }, [fetchJobs]);

  const addJob = useCallback((job) => {
    commitJobs((prev) => {
      if (prev.find((j) => j.id === job.id)) return prev;
      return [job, ...prev].sort((a, b) => new Date(b.queued_at || 0) < new Date(a.queued_at || 0) ? -1 : 1).slice(0, 20);
    });
  }, [commitJobs]);

  const handleConfirmClear = () => {
    localStorage.setItem('statsJobsClearedAt', Date.now().toString());
    commitJobs((prev) => prev.filter(job => ['queued', 'processing'].includes(job.status)));
    setClearDialogOpen(false);
  };

  const handleCancel = async (job) => {
    try {
      const isMl = Boolean(job.model_id) || job.job_type === 'ml' || job.job_type === 'machine_learning';
      if (isMl) {
        await axiosInstance.delete(`${API_PREFIX}/machine-learning/jobs/${job.id}`);
      } else {
        await axiosInstance.delete(`${API_PREFIX}/stats/jobs/${job.id}`);
      }
      commitJobs((prev) => prev.map((j) => j.id === job.id ? { ...j, status: 'cancelled' } : j));
      setTimeout(() => commitJobs((p) => p.filter((j) => j.id !== job.id)), 3000);
    } catch {
      // sessiz
    }
  };

  const handleJobClick = async (job) => {
    const cleaningHref = cleaningJobHref(job);
    if (cleaningHref) {
      setAnchorEl(null);
      navigate(cleaningHref);
      return;
    }
    if (job.status !== 'completed') return;
    setAnchorEl(null);
    try {
      const isMl = Boolean(job.model_id) || job.job_type === 'ml' || job.job_type === 'machine_learning' || job.category === 'ml';
      let fullJob;
      
      const isTrueMlJob = isMl && job.test_id !== 'profile_dataset' && job.test_id !== 'feature_impact';

      // Faz 4: tamamlanan istatistik işi doğrudan jobId ile açılır; sonuç ve künye
      // sihirbazın tek GET /stats/jobs/:id çağrısından gelir (ön-fetch yok, yeni run yok).
      if (!isMl && job.test_id !== 'profile_dataset' && job.test_id !== 'feature_impact' && job.job_type !== 'feature_impact') {
        navigate(`/statistical-analysis?jobId=${job.id}`);
        return;
      }

      if (isTrueMlJob) {
        const { data } = await axiosInstance.get(`${API_PREFIX}/machine-learning/jobs/${job.id}`);
        fullJob = data.result || data.job || data;
      } else {
        const { data } = await axiosInstance.get(`${API_PREFIX}/stats/jobs/${job.id}`);
        fullJob = data.job || data;
      }

      if (job.test_id === 'profile_dataset') {
        const fileId = fullJob.result?.file_id || fullJob.params?.fileId || fullJob.params?.file_id;
        const importId = fullJob.result?.excel_import_id || fullJob.params?.importJobId || fullJob.params?.excel_import_id;
        
        const basePath = isMl ? '/machine-learning' : '/statistical-analysis';
        
        if (fileId) navigate(`${basePath}?fileId=${fileId}`);
        else if (importId) navigate(`${basePath}?importJobId=${importId}`);

      } else if (job.test_id === 'feature_impact' || job.job_type === 'feature_impact') {
        navigate(`/feature-impact?jobId=${job.id}`);
      } else if (isMl) {
        navigate(`/machine-learning?jobId=${job.id}`);
      } else {
        navigate(`/statistical-analysis?jobId=${job.id}`);
      }
    } catch (err) {
      console.error('İş detayı alınırken hata:', err);
    }
  };

  const activeCount = jobs.filter((j) => ['queued', 'processing'].includes(j.status)).length;
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={statsJobsLabel}>
        <IconButton
          type="button"
          onClick={(e) => { setAnchorEl(e.currentTarget); fetchJobs(); }}
          aria-label={statsJobsLabel}
          sx={
            isPanel
              ? {
                  ...panelDropdownTriggerSx,
                  position: "relative",
                  animation: activeCount > 0 ? "statsJobPulse 1.5s infinite" : "none",
                  "@keyframes statsJobPulse": {
                    "0%, 100%": { boxShadow: `0 0 0 0 ${PANEL.accent.main}55` },
                    "50%": { boxShadow: `0 0 0 6px ${PANEL.accent.main}00` },
                  },
                }
              : {
                  backgroundColor: `${colors.blueAccent[700]}30`,
                  border: `1px solid ${colors.blueAccent[500]}40`,
                  position: "relative",
                  transition: "all 0.3s ease",
                  animation: activeCount > 0 ? "statsJobPulse 1.5s infinite" : "none",
                  "@keyframes statsJobPulse": {
                    "0%, 100%": { boxShadow: `0 0 0 0 ${colors.blueAccent[500]}55` },
                    "50%": { boxShadow: `0 0 0 6px ${colors.blueAccent[500]}00` },
                  },
                  "&:hover": {
                    backgroundColor: `${colors.blueAccent[600]}50`,
                  },
                }
          }
        >
          <Badge
            badgeContent={activeCount}
            color="primary"
            max={9}
            invisible={activeCount === 0}
            sx={isPanel ? panelDropdownBadgeSx : {
              "& .MuiBadge-badge": {
                fontWeight: 700,
                fontSize: "0.65rem",
                minWidth: 18,
                height: 18,
              },
            }}
          >
            <ScienceOutlinedIcon sx={isPanel ? panelTopbarActionIconSx : { color: colors.blueAccent[300] }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        {...(isPanel ? panelDropdownPopoverModalProps : { disableScrollLock: true, BackdropProps: { invisible: true } })}
        slotProps={isPanel ? panelDropdownPopoverSlotProps(360) : undefined}
        PaperProps={
          isPanel
            ? undefined
            : {
                sx: {
                  width: 360,
                  maxHeight: 480,
                  borderRadius: 2,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                },
              }
        }
      >
        <Box
          sx={
            isPanel
              ? panelDropdownHeaderSx
              : {
                  px: 2,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                }
          }
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ScienceOutlinedIcon fontSize="small" sx={{ color: isPanel ? PANEL.accent.main : "inherit" }} />
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={isPanel ? panelDropdownHeaderTitleSx : undefined}
            >
              {statsJobsLabel}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {loading && (
              <CircularProgress
                size={14}
                sx={{ color: isPanel ? PANEL.accent.main : "rgba(255,255,255,0.7)" }}
              />
            )}
            <Tooltip title={t("refresh")}>
              <IconButton
                size="small"
                onClick={fetchJobs}
                aria-label={t("refresh")}
                sx={isPanel ? panelDropdownHeaderActionSx : { color: "#fff", p: 0.5 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {!isPanel && <Divider />}

        <Box sx={isPanel ? { ...panelDropdownListSx, maxHeight: 380 } : { overflowY: "auto", maxHeight: 380 }}>
          {jobs.length === 0 ? (
            <Box sx={isPanel ? panelDropdownEmptySx : {
                py: 4,
                px: 2,
                minHeight: 140,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <ScienceOutlinedIcon
                sx={{
                  fontSize: 40,
                  color: isPanel ? PANEL.text.label : "text.disabled",
                  mb: 1,
                }}
              />
              <Typography
                variant="body2"
                sx={
                  isPanel
                    ? panelDropdownEmptyTitleSx
                    : { textAlign: "center", width: "100%", lineHeight: 1.5, color: "text.secondary" }
                }
              >
                {t("statsJobsEmpty")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {jobs.map((job, idx) => {
                const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG.queued;
                const isCleaning = job.job_type === CLEANING_JOB_TYPE;
                // Haritada olmayan (ör. klinik tarama/fenotipleme) testler katalogdaki yerelleştirilmiş ada düşer.
                let testName = TEST_NAMES[job.test_id]
                  || getTestLocalizedName(getCatalogTest(job.test_id), i18n.resolvedLanguage || i18n.language)
                  || job.test_id;
                if (!testName) {
                  if (job.model_id) {
                    const modelName = job.model_id
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (l) => l.toUpperCase());
                    testName = t('statsJobMlModel', { model: modelName });
                  } else if (job.job_type === 'ml' || job.job_type === 'machine_learning') {
                    testName = t('statsJobMachineLearning');
                  } else {
                    testName = t('statsAnalysisFallback');
                  }
                }
                if (isCleaning) testName = cleaningJobTitle(job, t);
                const queuedAt = job.queued_at
                  ? new Date(job.queued_at).toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' })
                  : '';
                const statusLabel = isCleaning && job.status === 'failed'
                  ? t('jobs.cleaning.statusFailed')
                  : t(cfg.labelKey);
                const liveProgress = progressMap[job.id] != null ? progressMap[job.id] : job.progress;
                const displayLabel = job.status === 'processing' && liveProgress
                  ? t('statsJobProcessingPercent', { percent: liveProgress })
                  : statusLabel;

                const colorMap = {
                  warning: 'yellowAccent',
                  info: 'blueAccent',
                  success: 'greenAccent',
                  error: 'redAccent',
                  default: 'grey'
                };
                const themeColor = colorMap[cfg.color] || 'blueAccent';
                const isRunning = ['queued', 'processing'].includes(job.status);
                const canCancel = isRunning && !isCleaning;
                const clickable = isCleaning
                  ? job.status === 'completed' || job.status === 'failed'
                  : job.status === 'completed';
                const cleaningLine = isCleaning
                  ? [queuedAt, cleaningJobDetail(job, t, language)].filter(Boolean).join(' • ')
                  : null;

                return (
                  <Box key={`${job.job_type || 'job'}:${job.id}`} data-testid={`stats-job-${job.id}`}>
                    {idx > 0 && <Divider sx={isPanel ? panelDropdownDividerSx : undefined} />}
                    <Box
                      sx={
                        isPanel
                          ? panelDropdownItemRowSx(isRunning)
                          : {
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 0.5,
                              backgroundColor: isRunning ? `${colors[themeColor]?.[900] || colors.blueAccent[900]}22` : "transparent",
                              borderLeft: isRunning
                                ? `3px solid ${colors[themeColor]?.[500] || colors.blueAccent[500]}`
                                : "3px solid transparent",
                              transition: "background-color 0.15s ease",
                              "&:hover": {
                                backgroundColor: `${colors.blueAccent[800]}44`,
                              },
                            }
                      }
                    >
                      <Box
                        component="button"
                        type="button"
                        onClick={() => handleJobClick(job)}
                        sx={
                          isPanel
                            ? panelDropdownItemButtonSx
                            : {
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                gap: 1.5,
                                px: 2,
                                py: 1.5,
                                pr: 0.5,
                                textAlign: "left",
                                border: "none",
                                cursor: clickable ? "pointer" : "default",
                                backgroundColor: "transparent",
                                color: "inherit",
                              }
                        }
                      >
                        <Box
                          sx={
                            isPanel
                              ? panelDropdownItemIconWrapSx(
                                  cfg.color === "success"
                                    ? PANEL.accent.main
                                    : cfg.color === "error"
                                      ? PANEL.danger.main
                                      : cfg.color === "warning"
                                        ? "#d97706"
                                        : PANEL.blue.main
                                )
                              : {
                                  mt: 0.25,
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1.5,
                                  flexShrink: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: `${colors[themeColor]?.[800] || colors.grey[800]}55`,
                                  border: `1px solid ${colors[themeColor]?.[600] || colors.grey[600]}44`,
                                  color: colors[themeColor]?.[400] || colors.grey[400],
                                }
                          }
                        >
                          {cfg.icon}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.35 }}>
                            <Typography
                              variant="body2"
                              sx={
                                isPanel
                                  ? { ...panelDropdownItemTitleSx(isRunning), whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
                                  : {
                                      fontWeight: 600,
                                      color: colors.grey[100],
                                      lineHeight: 1.35,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }
                              }
                            >
                              {testName}
                            </Typography>
                            <Chip
                              label={displayLabel}
                              color={cfg.color}
                              size="small"
                              sx={{ fontSize: "0.65rem", height: 18 }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={
                              isPanel
                                ? panelDropdownItemTimeSx
                                : {
                                    color: colors.blueAccent[200],
                                    display: "block",
                                    fontSize: "0.72rem",
                                    fontWeight: 500,
                                    opacity: 0.9,
                                  }
                            }
                          >
                            {cleaningLine != null
                              ? cleaningLine
                              : (
                                <>
                                  {queuedAt && `${queuedAt} • `}
                                  {job.cost ? t('statsJobCostUnits', { cost: Number(job.cost).toFixed(2) }) : ''}
                                </>
                              )}
                          </Typography>
                        </Box>
                      </Box>

                      {canCancel && (
                        <Box sx={{ py: 1.5, pr: 1.5 }}>
                          <Tooltip title={t('statsJobCancel')}>
                            <IconButton
                              size="small"
                              onClick={() => handleCancel(job)}
                              aria-label={t('statsJobCancel')}
                              sx={{
                                color: colors.redAccent[300],
                                border: `1px solid ${colors.redAccent[500]}55`,
                                borderRadius: 1.5,
                                p: 0.5,
                                "&:hover": { backgroundColor: `${colors.redAccent[900]}44` },
                              }}
                            >
                              <CancelIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {jobs.length > 0 && (
          <>
            {!isPanel && <Divider />}
            <Box sx={isPanel ? panelDropdownFooterSx : { px: 2, py: 1, display: "flex", justifyContent: "space-between" }}>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setAnchorEl(null);
                  navigate("/analysis-history");
                }}
                sx={isPanel ? panelDropdownFooterButtonSx : { fontSize: "0.72rem", color: colors.blueAccent[400] }}
              >
                {t("statsJobsSeeAll")}
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => setClearDialogOpen(true)}
                sx={
                  isPanel
                    ? { ...panelDropdownFooterButtonSx, color: PANEL.text.muted, "&:hover": { backgroundColor: PANEL.pageBg } }
                    : { fontSize: "0.72rem", color: "text.secondary" }
                }
              >
                {t("statsJobsClearList")}
              </Button>
            </Box>
          </>
        )}
      </Popover>

      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        PaperProps={{
          style: {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle>{t('statsJobsClearList') || 'Listeyi Temizle'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Listeyi temizlemek istediğinize emin misiniz? Devam eden analizleriniz silinmeyecektir, sadece tamamlanan işlerin bildirimleri temizlenecektir.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            İptal
          </Button>
          <Button onClick={handleConfirmClear} color="error" autoFocus>
            Temizle
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(cleaningToast)}
        autoHideDuration={6000}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return;
          setCleaningToast(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {cleaningToast ? (
          <Alert
            severity={cleaningToast.status === 'failed' ? 'error' : 'success'}
            onClose={() => setCleaningToast(null)}
            onClick={() => {
              const href = cleaningJobHref({
                id: cleaningToast.id,
                job_type: CLEANING_JOB_TYPE,
                status: cleaningToast.status,
                dataset_id: cleaningToast.dataset_id,
              });
              setCleaningToast(null);
              if (href) navigate(href);
            }}
            sx={{ cursor: 'pointer' }}
          >
            {cleaningToast.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      {/* Ref export for parent to call addJob */}
      <span ref={(el) => { if (el) el.addJob = addJob; }} style={{ display: 'none' }} />
    </>
  );
}

/**
 * useStatsJob hook — İstatistik isteğini hibrit routing ile gönderir.
 *
 * @returns {{ runStats, loading, jobId, result, error, target }}
 */
export function useStatsJob({ onJobQueued }) {
  const sessionActive = isDoctorSessionActive();
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [target, setTarget] = useState(null);
  const workerRef = useRef(null);

  const BROWSER_TESTS = new Set([
    'descriptive', 'paired_t', 'paired_t_test', 'independent_t', 'welch_t', 'pearson', 'spearman',
  ]);

  const runStats = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    setResult(null);
    setJobId(null);
    setTarget(null);

    if (!sessionActive) {
      const msg = 'Oturum bulunamadı';
      setError(msg);
      setLoading(false);
      return { success: false, error: msg };
    }

    const testId = payload.test || payload.testId;

    try {
      // 1. COST tahmini al
      const estRes = await axiosInstance.post(`${API_PREFIX}/stats/estimate`, {
        testId,
        rowCount: payload.rowCount || 500,
        dynamicFieldCount: 0,
        columnCount: 2,
      });

      const { estimate } = estRes.data;
      setTarget(estimate.target);

      // 2. Browser target — prefer_browser ile gönder
      if (estimate.target === 'browser' && BROWSER_TESTS.has(testId)) {
        const runRes = await axiosInstance.post(`${API_PREFIX}/statistical-tests/run`, {
          ...payload,
          prefer_browser: true,
        });

        if (runRes.data.target === 'browser' && runRes.data.browserData) {
          // Web Worker'da hesapla
          const { test, params, columns, rows } = runRes.data.browserData;
          const workerResult = await runInWorker({ test, params, columns, rows });
          setResult(workerResult);
          setLoading(false);
          return { success: true, result: workerResult, target: 'browser' };
        }
        // Fallback: sunucudan geldi
        setResult(runRes.data.result);
        setLoading(false);
        return { success: true, result: runRes.data.result, target: 'server_sync' };
      }

      // 3. Server path (sync veya async)
      const runRes = await axiosInstance.post(`${API_PREFIX}/statistical-tests/run`, payload);

      if (runRes.status === 202) {
        // Async — jobId ile bildir
        const { jobId: jId } = runRes.data;
        setJobId(jId);
        setLoading(false);
        if (onJobQueued) onJobQueued(runRes.data);
        return { success: true, queued: true, jobId: jId, target: 'server_async' };
      }

      setResult(runRes.data.result);
      setLoading(false);
      return { success: true, result: runRes.data.result, target: 'server_sync' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'İstatistik hatası';
      setError(msg);
      setLoading(false);
      return { success: false, error: msg };
    }
  }, [sessionActive, onJobQueued]); // eslint-disable-line react-hooks/exhaustive-deps

  async function runInWorker(data) {
    return new Promise((resolve, reject) => {
      if (!workerRef.current) {
        workerRef.current = createStatsWorker();
      }
      const jid = Math.random().toString(36).slice(2);
      workerRef.current.postMessage({ jobId: jid, ...data });
      workerRef.current.onmessage = (e) => {
        if (e.data.jobId === jid) {
          if (e.data.success) resolve(e.data.result);
          else reject(new Error(e.data.error));
        }
      };
    });
  }

  return { runStats, loading, jobId, result, error, target };
}
